import { OrderedSet } from 'immutable';
import { createSelector, Selector } from 'reselect';
import core, { View, GQL } from '@gin-betting/core';
import banners from '@gin-betting/banners';

import { selectRouteBySportEvent } from '../../selectors';
import type { PromoState, BannerItemsByZoneId } from './types';
import type { State } from 'types';
import {
    oddsPropsToPick,
    matchPropsToPick,
    marketPropsToPick,
    tournamentPropsToPick,
} from './constants';
import { groupBannerItemsBySport } from './utils';

const {
    entities: {
        selectors: {
            selectMatchCompetitorsValid,
            selectTournaments,
            selectSports,
            makeMatchSelector,
            makeMarketSelector,
            makeOddsByMarketSelector,
            makeTournamentSelector,
        },
    },
} = core;

const {
    selectors: { makeBannerItemsSelector },
} = banners;

export const selectPromo = (state: State): PromoState => state.promo;

export const selectStateTopTournaments = createSelector(
    selectPromo,
    promoState => promoState.get('topTournaments')
);

export const selectTopTournamentIds = createSelector(
    selectStateTopTournaments,
    (topTournaments): OrderedSet<string> => topTournaments.tournaments
);

export const selectTopTournaments = createSelector(
    selectTopTournamentIds,
    selectTournaments,
    (topTournamentsIds, tournaments): OrderedSet<View.Tournament> => {
        return topTournamentsIds
            .filter(topTournament => tournaments.has(topTournament))
            .map(topTournament => tournaments.get(topTournament)!);
    }
);

export const selectExistedTopTournamentIds = createSelector(
    selectTopTournamentIds,
    selectTournaments,
    (topTournamentsIds, tournaments) =>
        topTournamentsIds.filter(topTournament =>
            tournaments.has(topTournament)
        )
);

export const selectAreSportsAvailable = createSelector(selectSports, sports =>
    Boolean(sports.size)
);

const selectMatch = makeMatchSelector({
    propsToPick: matchPropsToPick,
});

const selectMarket = makeMarketSelector({
    propsToPick: marketPropsToPick,
});

const selectOdds = makeOddsByMarketSelector({
    marketSelector: selectMarket,
    propsToPick: oddsPropsToPick,
});

const selectTournament = makeTournamentSelector({
    propsToPick: tournamentPropsToPick,
    selectTournamentId: createSelector(selectMatch, match =>
        match ? match.tournament : null
    ),
});

export const makePropsSelector = () =>
    createSelector(
        selectMatch,
        selectMarket,
        selectOdds,
        selectTournament,
        selectMatchCompetitorsValid,
        selectRouteBySportEvent,
        (match, market, odds, tournament, competitors, route) => ({
            match,
            market,
            odds,
            tournament,
            competitors,
            route,
        })
    );

export const makeSelectZones = (
    zoneIds: string[]
): Selector<any, BannerItemsByZoneId> => {
    const bannerItemSelectors = zoneIds.map(zoneId =>
        makeBannerItemsSelector(zoneId, GQL.BannerType.betting_sport_event)
    );

    // NOTE: "createSelector" type issue with rest args
    return (createSelector as any)(
        ...bannerItemSelectors,
        (
            ...bannerItemsArr: Array<
                ReturnType<typeof bannerItemSelectors[number]>
            >
        ): BannerItemsByZoneId => {
            // NOTE: zoneIds.length should be equal with bannerItemsArr.length
            return zoneIds.reduce<BannerItemsByZoneId>((acc, zoneId, index) => {
                const prev = acc[zoneId];

                const bannerItems = bannerItemsArr[index];

                if (!bannerItems) return acc;

                acc[zoneId] = groupBannerItemsBySport(bannerItems, prev);

                return acc;
            }, {});
        }
    );
};
